import { Link, useSearch } from "@tanstack/react-router";
import { SearchX } from "lucide-react";
import type * as React from "react";

import { BackLink } from "@/components/shared/back-link";
import { useSetBreadcrumbs } from "@/components/shared/breadcrumbs";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { useSetOrigin } from "@/components/shared/origin-context";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { decodeListSearch, originTarget, parseOriginSearch } from "@/lib/detail-origin";
import { cn } from "@/lib/utils";

/**
 * The shape every detail page's `useX(id)` query already has. Declared
 * structurally rather than as TanStack's `UseQueryResult` so a page can hand
 * its query straight in without naming the generic.
 */
interface DetailQuery<T> {
  data: T | null | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

interface EntityDetailPageProps<T> {
  query: DetailQuery<T>;
  /**
   * The singular noun for this record, Title Case — "Skill", "Person". Used for
   * the breadcrumb before the name loads ("Skill"), the not-found heading
   * ("Skill not found") and, lowercased, the load failure ("Failed to load skill").
   */
  entityLabel: string;
  /** The top breadcrumb, naming the area — "Capability", "Roles". */
  section: string;
  /** The catalog this record belongs to; the back link and middle crumb. */
  list: { label: string; to: string };
  /** The record's own name once loaded. Falls back to `entityLabel`. */
  name: string | undefined;
  /** Container spacing override. Only Person needs one — see its page. */
  className?: string;
  /**
   * Render the page at the layout's wide width. Belongs at the page root, not
   * on one tab: the layout widens as soon as any descendant carries the marker,
   * so a per-tab marker snaps the panel between widths as you move between tabs.
   */
  wide?: boolean;
  children: (entity: T) => React.ReactNode;
}

/**
 * The frame every record page shares: breadcrumbs, the back link, and the
 * loading / failed / not-found states. Five pages had byte-identical copies of
 * all of it and had already started to drift apart.
 *
 * The page keeps everything specific to the record — its header, tabs and
 * sections — and gets the loaded entity through the render-prop child, already
 * narrowed to non-null.
 *
 * Where the reader came from is read here rather than passed in. A page that
 * also needs it (to open a deep-linked tab, say) calls `parseOriginSearch`
 * itself; it is a pure read of the same search params, so both agree.
 */
export function EntityDetailPage<T>({
  query,
  entityLabel,
  section,
  list,
  name,
  className,
  wide = false,
  children,
}: EntityDetailPageProps<T>) {
  const parsed = parseOriginSearch(useSearch({ strict: false }));
  const origin = originTarget(parsed);
  const listSearch = decodeListSearch(parsed.lf);

  useSetBreadcrumbs(
    origin
      ? [origin, { label: name ?? entityLabel }]
      : [
          { label: section },
          { label: list.label, to: list.to, search: listSearch },
          { label: name ?? entityLabel },
        ],
  );
  // Links out of this record (Department, Role Team, …) send the reader back here.
  useSetOrigin(name);

  if (query.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-9 w-72" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <ErrorState
        message={query.error?.message ?? `Failed to load ${entityLabel.toLowerCase()}`}
        onRetry={() => query.refetch()}
      />
    );
  }

  if (query.data == null) {
    return (
      <EmptyState
        icon={SearchX}
        title={`${entityLabel} not found`}
        description="It may have been removed, or the link may be out of date."
        action={
          <Button asChild variant="outline" size="sm">
            <Link to={list.to}>Back to {list.label}</Link>
          </Button>
        }
      />
    );
  }

  return (
    <div className={cn("space-y-4", className)} data-wide-page={wide ? "" : undefined}>
      {origin ? (
        <BackLink
          to={origin.to}
          params={origin.params}
          search={origin.search}
          label={origin.label}
        />
      ) : (
        <BackLink to={list.to} search={listSearch} label={list.label} />
      )}
      {children(query.data)}
    </div>
  );
}
